import { useApiMutation } from "@gladis/api-client";
import { Button, Label, MoneyInput, TextField, parseEurInput } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import { PROPERTIES_KEY } from "./PropertyList.js";

/**
 * Mark a property sold (FR-048, FR-049).
 *
 * ONE STEP, and that is the whole reason it exists. A household could record the sale price as a
 * valuation and then archive the property, in two actions — and a half-completed version of that is
 * a valuation recorded for something that did not happen.
 *
 * THE SALE DATE DEFAULTS TO TODAY, CLIENT-SIDE. The server supplies no default, because the sale
 * date is the date of a real event rather than the date the form was submitted, and the server's
 * today is not necessarily the household's.
 *
 * Nothing here says the property will become indistinguishable from one archived by mistake — the
 * copy says what happens, and the archived list is where that consequence shows up. It is recorded
 * in `ArchivedProperties` rather than as a warning here, because warning someone about a limitation
 * of the activity-log reader at the moment they sell a house is not useful to them.
 */
export function SellPropertyDialog({
  propertyId,
  onSold,
  onCancel,
}: {
  propertyId: string;
  onSold?: () => void;
  onCancel?: () => void;
}): ReactNode {
  const t = useT();
  const [salePrice, setSalePrice] = useState("");
  const [saleDate, setSaleDate] = useState(() => new Date().toISOString().slice(0, 10));

  const sell = useApiMutation<{ archivedPartIds: string[] }, Record<string, unknown>>(
    (client, input) =>
      client.post<{ archivedPartIds: string[] }>(`/properties/${propertyId}/sell`, input),
    { invalidates: [PROPERTIES_KEY] },
  );

  const amount = parseEurInput(salePrice);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (amount === null) return;
    sell.mutate({ salePrice: amount, saleDate }, { onSuccess: () => onSold?.() });
  }

  return (
    /*
     * NO CARD CHROME, NO HEADING, NO DESCRIPTION — all three are the dialog's.
     *
     * THE NAME IS A LEFTOVER. This component was called `SellPropertyDialog` while being a bordered
     * panel rendered inline, and it imports no `Dialog` even now. When the property page moved its
     * actions into the title row it started being rendered from outside `Page.Body`, where a bare
     * card floats under the footer — the name promised a modal and the caller believed it. The
     * caller supplies the real `Dialog`; this is the form that goes inside one.
     */
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="sale-price">{t("prop.sell.price")}</Label>
          <MoneyInput id="sale-price" value={salePrice} onValueChange={setSalePrice} required />
        </div>
        <div>
          <Label htmlFor="sale-date">{t("prop.sell.date")}</Label>
          <TextField
            id="sale-date"
            type="date"
            value={saleDate}
            onChange={(event) => setSaleDate(event.target.value)}
            required
          />
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Button type="submit" variant="primary" disabled={amount === null || sell.isPending}>
          {sell.isPending ? t("common.saving") : t("prop.sell.heading")}
        </Button>
        <Button onClick={() => onCancel?.()}>{t("common.cancel")}</Button>
        {sell.isError ? (
          <span className="text-sm text-danger">{errorSentence(sell.error)}</span>
        ) : null}
      </div>
    </form>
  );
}
